/* oxlint-disable react-hooks/immutability */
import { functionalUpdate, type Updater } from '@tanstack/react-table';
import { useCallback } from 'react';

import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import type { DataTableRowData } from '../../row-data-types.js';
import type { DataTableOptions } from '../types/table.types.js';
import type {
  DataTableColumnSizingProps,
  DataTableColumnSizingState,
} from './column-sizing-types.js';

/**
 * Configuration hook for column sizing feature, to manage the column sizing state and enable column resizing in the table options.
 * @internal
 */
export function useColumnSizing<TData extends DataTableRowData>(
  props: DataTableColumnSizingProps,
  options: DataTableOptions<Exclude<TData, unknown>>,
): void {
  options.enableColumnResizing = false;

  const configResizable = useDataTableConfigElement('resizable');

  const [columnSizing, setColumnSizing] = useControllableState({
    value: props.columnSizing,
    defaultValue: props.defaultColumnSizing ?? {},
    onChange: props.onColumnSizingChange,
  });

  const onColumnSizingChangeCallback = useCallback(
    (updater: Updater<DataTableColumnSizingState>) => {
      const newValue = functionalUpdate(updater, columnSizing);
      setColumnSizing(newValue);
    },
    [setColumnSizing, columnSizing],
  );

  if (configResizable === true || props.resizable === true) {
    options.enableColumnResizing = true;
    options.state = {
      ...options.state,
      columnSizing,
    };

    options.onColumnSizingChange = onColumnSizingChangeCallback;
  }
}
